// Standard Library Imports
import React, { useEffect } from 'react';
import { useState } from 'react';

// External Library Imports
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';
import { get as lodashGet, set as lodashSet } from 'lodash';

// Internal Module Imports
import Dropdown from '../../../components/Inputs/Dropdown';
import { useLoader } from '../../../hooks/use-loader.hook';
import { getTotalHoursForDay, momentTimeFormater } from '../../../utils/dateHelper';
import Typography from '../../../components/Typography/Typography';
import { Input, TextArea, ToggleButton } from '../../../components/Inputs/Input';
import { RequestBidValidation } from '../../../validations/BidRequest/RequestBidvalidation';
import DatePickerWithFormik from '../../../components/Inputs/DatePicker/DatePickerWithFormik';
// CSS Imports
import classes from '../../../styles/BidModal.module.css';
import moment from 'moment';
import { findDiffInDays } from '../../../utils/timeHelper';
import { BidRequestService } from '../../../services/Bidding/BidRequest';

const getInstanceOptions = (instances = []) => {
    const options = [];
    instances.forEach((g) => {
        options.push({ label: g.name, value: g._id });
    });
    return options;
};

const getCapacityAndPrice = (editRequest, market, date) => {
    const diff = findDiffInDays(momentTimeFormater(date.startDate).format('YYYY-MM-DD'), momentTimeFormater(date.endDate).format('YYYY-MM-DD'));

    let maxTotalHours = getTotalHoursForDay(momentTimeFormater(date.startDate).format('YYYY-MM-DD'));

    diff > 0 &&
        Array(diff)
            .fill(null)
            .map((__, idx) => {
                const totalHours = getTotalHoursForDay(
                    momentTimeFormater(date.startDate)
                        .add(idx + 1, 'day')
                        .format('YYYY-MM-DD')
                );
                maxTotalHours = Math.max(maxTotalHours, totalHours);
            });

    const temp = {
        capacity: editRequest.capacity || [],
        price: editRequest.price || [],
        ...(market.includes('aFRR') || market.includes('mFRR') ? { regulationPrice: editRequest.regulationPrice || [] } : {}),
    };

    Array(maxTotalHours)
        .fill(0)
        .map((__, idx) => {
            lodashSet(temp, ['capacity', idx], lodashGet(editRequest, ['capacity', idx], 0));
            lodashSet(temp, ['price', idx], lodashGet(editRequest, ['price', idx], 0));
            if (market.includes('aFRR') || market.includes('mFRR')) lodashSet(temp, ['regulationPrice', idx], lodashGet(editRequest, ['regulationPrice', idx], 0));
        });

    return temp;
};

export const CreateEditBidRequest = ({ instances = [], market, setOpen, editRequest = {}, licenseId, fetchRequests }) => {
    const [startLoader, stopLoader] = useLoader();
    const instanceOptions = getInstanceOptions(instances);
    const [date, setDate] = useState({
        startDate: editRequest?.startDate ? momentTimeFormater(editRequest.startDate).toDate() : moment().toDate(),
        endDate: editRequest?.endDate ? momentTimeFormater(editRequest.endDate).toDate() : moment().toDate(),
    });
    const [initialValues, setInitialValues] = useState({
        ...getCapacityAndPrice(editRequest, market, date),
        instanceId: editRequest.instanceId || '',
        startDate: date.startDate,
        endDate: date.endDate,
        biddingMarket: editRequest.biddingMarket || '',
        note: editRequest?.note || '',
        customBidding: editRequest?.customBidding || false,
    });

    const [customBidding, setCustomBidding] = useState(editRequest?.customBidding || false);

    useEffect(() => {
        updateField(editRequest, date);
    }, []);

    const updateField = (updatedValues, date) => {
        const values = getCapacityAndPrice(updatedValues, market, date);
        setInitialValues({ ...initialValues, ...values });
    };

    const handleSubmit = (values) => {
        values.licenseId = licenseId;
        values.biddingMarket = market;
        values.customBidding = customBidding;
        values.startDate = momentTimeFormater(date.startDate).format('YYYY-MM-DD');
        values.endDate = momentTimeFormater(date.endDate).format('YYYY-MM-DD');
        values.capacity = values.customBidding ? values.capacity : [];
        values.price = values.customBidding ? values.price : [];
        if (market.includes('aFRR') || market.includes('mFRR')) values.regulationPrice = values.customBidding ? values.regulationPrice : [];

        if (!editRequest._id) BidRequestService.CreateRequest(values, startLoader, handleCreateSuccess, handleError, stopLoader);
        else BidRequestService.UpdateRequest(editRequest._id, values, startLoader, handleUpdateSuccess, handleError, stopLoader);
    };

    const handleCreateSuccess = ({}) => {
        toast.success('Bid request created successfully!');
        setOpen(false);
        fetchRequests();
    };

    const handleUpdateSuccess = ({}) => {
        toast.success('Bid request updated successfully!');
        setOpen(false);
        fetchRequests();
    };

    const handleError = (err) => {
        if (err) toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    return (
        <div>
            <div style={{ minWidth: '35vw' }}>
                <Typography content={`${editRequest._id ? 'Edit' : 'Create'} Request`} />
                <div className={classes.FormContainer}>
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={RequestBidValidation(market, customBidding)} enableReinitialize>
                        {({ values, setFieldValue }) => (
                            <Form>
                                <div>
                                    <div className={classes.Subtitle}>Config</div>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl} style={{ flexGrow: 1 }}>
                                            <label>
                                                Instance <span className="required">*</span>
                                            </label>
                                            <Dropdown
                                                name="instanceId"
                                                options={instanceOptions}
                                                defaultValue={initialValues.instanceId ? instanceOptions.filter((ins) => ins.value === values.instanceId)[0] : null}
                                            />
                                        </div>
                                    </div>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label>
                                                Start Date <span className="required">*</span>
                                            </label>
                                            <div className="modal-date-picker">
                                                <DatePickerWithFormik
                                                    name={`startDate`}
                                                    containerStyle={{ height: '2.5vw', width: '100%' }}
                                                    date={initialValues?.startDate}
                                                    setDate={(e) => {
                                                        setDate({ ...date, startDate: e });
                                                        updateField(values, e);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className={classes.FieldControl2}>
                                            <label>
                                                End Date <span className="required">*</span>
                                            </label>
                                            <div className="modal-date-picker">
                                                <DatePickerWithFormik
                                                    name={`endDate`}
                                                    containerStyle={{ height: '2.5vw', width: '100%' }}
                                                    date={initialValues?.endDate}
                                                    setDate={(e) => {
                                                        setDate({ ...date, endDate: e });
                                                        updateField(values, e);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.InputContainer}>
                                    <div className={classes.FieldControl2}>
                                        <ToggleButton
                                            label={'Custom Bidding'}
                                            labelPosition={'left'}
                                            values={values}
                                            name="customBidding"
                                            id="customBidding"
                                            onChange={(e) => {
                                                setFieldValue('customBidding', e.target.checked);
                                                setCustomBidding(e.target.checked);
                                            }}
                                        />
                                    </div>
                                </div>
                                {values.customBidding && (
                                    <>
                                        {[
                                            {
                                                label: 'Capacity (Mw)',
                                                value: 'capacity',
                                            },
                                            {
                                                label: 'Price (€)',
                                                value: 'price',
                                            },
                                            ...(market.includes('aFRR') || market.includes('mFRR')
                                                ? [
                                                      {
                                                          label: 'Regulation Price (€)',
                                                          value: 'regulationPrice',
                                                      },
                                                  ]
                                                : []),
                                        ].map((type) => (
                                            <div>
                                                <div className={classes.Subtitle}>{type.label}</div>

                                                <div className={classes.CreateBidForm}>
                                                    {lodashGet(values, [type.value]).map((val, index) => (
                                                        <div>
                                                            <div className={classes.FieldControl}>
                                                                <label htmlFor={`${type.value}.${index}`}>
                                                                    Slot {index + 1} {' / Hour '}
                                                                    {index} - {index + 1}
                                                                    {type.value === 'price' ? ' Price' : ' '}
                                                                    <span className="required">*</span>
                                                                </label>
                                                                <Input
                                                                    name={`${type.value}.${index}`}
                                                                    id={`${type.value}.${index}`}
                                                                    type={'number'}
                                                                    style={{ marginTop: '0' }}
                                                                    step="0.1"
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                                <div className={classes.FieldControl2}>
                                    <label for="note">
                                        Note <span className="required">*</span>
                                    </label>
                                    <TextArea name={`note`} label="Note" />
                                </div>

                                <div className={classes.ButtonContainer}>
                                    <div>
                                        <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
                                            Cancel
                                        </button>
                                    </div>
                                    <div>
                                        <button type="submit" className="btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};
